import React, { ReactElement, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { forDesktop, forTablet } from '@westwing/ui-kit/breakPoints';
import { header1, header2 } from '@westwing/ui-kit/typography';
import TextTruncator from '@westwing/ui-kit/TextTruncator';
import { LooksPageUrlState, PageType, urlStateToWebUrl } from '@westwing/looks-url-service';
import { parse } from 'query-string';
import LooksGrid from './LooksGrid';
import Breadcrumbs from './Breadcrumbs';
import FilterAndSortBox from './FilterAndSortBox';
import BackButton from './BackButton';
import SeoLinks from './SeoLinks';
import { LooksSeoHead } from './SeoHead';
import LooksNotFound from './LooksNotFound';
import { TrackLooksActions } from 'Common/ga/types';
import BubblesSlider from 'Client/components/Generic/BubblesSlider';
import ErrorBoundary from 'Client/components/Generic/ErrorBoundaries';
import GenericErrorRetry from 'Client/components/Generic/ErrorPages/GenericErrorRetry';
import { fetchLooksListingIfNeeded } from 'Client/redux/looksPage/actions';
import { fetchLooksPromotionIfNeeded } from 'Client/redux/looksPromotionalElements/actions';
import useFetchingAction from 'Client/hooks/useFetchingAction/useFetchingAction';
import StateInterface from 'Client/redux/types';
import { LooksListingFilterParams, LooksListingProcessedData } from 'Client/redux/looksPage/types';
import { useLooksTracking } from 'Client/components/Looks/hooks/useLooksTracking';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { showOverlay } from 'Client/redux/overlays/actions';
import { OverlayId } from 'Client/components/Overlays/types';
import useRouter from 'Client/hooks/useRouter';
import { textLinesLimitClipCss } from 'Client/components/Generic/Text/StyledText';
import StatusCodeComponentWrapper from 'Client/components/StaticContent/StatusCodeComponentWrapper';
import NotFound from 'Client/components/NotFound';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { midWideScreenPadding } from 'Client/components/Generic/BoxHelpers';
import { combineThunks } from 'Client/redux/utils';

const PageWrapper = styled.div<{ p100IncreasedDesktopPageWidth?: boolean }>`
    min-height: 90vh;
    ${forTablet`
        padding: 0 15px;
    `}
    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            ${midWideScreenPadding}
        `}
`;

const LooksHeaderWrapper = styled.div`
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const TitleRow = styled.div`
    display: flex;
    margin-top: 20px;
    ${forDesktop`
        margin-top: 30px;
    `}
`;

const Title = styled.h1`
    ${header1}
    min-height: 33px;
    text-align: center;
    margin: 0;
    flex-grow: 1;
    padding-right: 40px;
    ${textLinesLimitClipCss(2)}
    ${forTablet`
        padding: 0 ${({ theme }) => theme.tabletPagePadding};
    `}
    ${forDesktop`
        ${header2}
        min-height: 38px;
    `}
`;

const StyledTextTruncator = styled(TextTruncator)`
    min-height: 21px;
`;

const StyledDescription = styled.div`
    margin: 5px 30px 0 30px;
`;

const RoomsSlider = styled(BubblesSlider)`
    height: 42px;
    min-height: 42px;
    overflow-y: hidden;
    margin: 26px 0 29px;

    ${forTablet`
        margin: 26px 0 23px;
        height: auto;
        min-height: 47px;
        display: flex;
        justify-content: center;
        flex-flow: wrap;
    `}
`;

interface LooksListingPageProps {
    initialRoom?: string;
    initialStyle?: string;
}

// eslint-disable-next-line complexity
const LooksListingPage: React.FC<LooksListingPageProps> = ({ initialRoom, initialStyle }): ReactElement => {
    const dispatch = useDispatch();
    const { LLP: LLPTracking } = useLooksTracking();
    const { p100IncreasedDesktopPageWidth } = useFeatureToggle();
    const {
        location: { pathname, search },
    } = useRouter();
    const t = useBellaTranslator();
    const {
        meta: { name, caption },
        filters: { rooms, styles },
    } = useSelector<StateInterface, LooksListingProcessedData>(state => state.looksPage.looks);
    const looksPerPage = useSelector<StateInterface, number | undefined>(state => state.looksPage.looks?.perPage);
    const looksTotalAmount = useSelector<StateInterface, number>(state => state.looksPage.looks?.total);
    const isFetching = useSelector<StateInterface, boolean | undefined>(state => state.looksPage.isFetching);
    const identifier = useSelector<StateInterface, string>(state => state.looksPage.identifier);
    const looksError = useSelector<StateInterface, any>(state => state.looksPage.error);
    const filteredRoom = initialRoom;
    const filteredStyle = initialStyle;
    const parsedQuery: { page?: number; [key: string]: string | string[] | number | undefined | null } =
        parse(search) || {};

    const params: LooksListingFilterParams = {
        ...(looksPerPage ? { perPage: looksPerPage } : {}),
        room: filteredRoom,
        style: filteredStyle,
        ...parsedQuery,
        page: Number(parsedQuery.page) || 1,
    };

    if (filteredRoom) {
        params.room = filteredRoom;
    }

    const looksUrlBuilderParams: LooksPageUrlState = {
        pageType: PageType.LLP,
    };

    if (filteredStyle) {
        looksUrlBuilderParams.styles = [filteredStyle];
    }

    useFetchingAction(combineThunks(fetchLooksListingIfNeeded(params), fetchLooksPromotionIfNeeded()));

    const roomsWithLinks = rooms.map(room => {
        return {
            ...room,
            link: urlStateToWebUrl({
                pageType: PageType.LLP,
                styles: filteredStyle ? [filteredStyle] : [],
                rooms: room.isSelected ? [] : [room.slug],
            }),
        };
    });
    const selectedRoom = roomsWithLinks.filter(room => room.isSelected)[0];
    const selectedStyle = styles.filter(style => style.selected)[0];

    const handleRoomItemClick = (roomName: string) => {
        const selectedTrackingRoom = rooms.find(room => room.name === roomName);

        if (selectedTrackingRoom) {
            LLPTracking.track(TrackLooksActions.LLPCategoryBubble, selectedTrackingRoom.trackingName);
        }
    };

    const handleDescriptionSizeToggle = () => {
        LLPTracking.track(TrackLooksActions.LLPDescriptionReadMore, pathname);
    };

    useEffect(() => {
        const isDataFetched = identifier !== '' && !isFetching;
        if (isDataFetched) {
            LLPTracking.trackPageView(selectedRoom, selectedStyle, params.page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching, identifier]);

    const handleFiltersClick = () => {
        dispatch(showOverlay(OverlayId.LOOKS_FILTERS_FLYOUT));
    };

    if (looksError && !isFetching) {
        return (
            <StatusCodeComponentWrapper code={looksError.code || 404}>
                <NotFound />
            </StatusCodeComponentWrapper>
        );
    }

    return (
        <ErrorBoundary boundaryName="Looks Page" fallbackComponent={<GenericErrorRetry />}>
            <LooksSeoHead />
            <SeoLinks total={looksTotalAmount} perPage={looksPerPage} path={pathname} />
            <PageWrapper data-testid="looks-listing-page" p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}>
                <Breadcrumbs room={selectedRoom?.name} />
                <LooksHeaderWrapper>
                    <TitleRow>
                        <BackButton to="/looks/" isVisible={!!selectedRoom} />
                        <Title>{name}</Title>
                    </TitleRow>
                    <StyledDescription>
                        <StyledTextTruncator
                            text={caption}
                            readMoreText={`(${t('read more')})`}
                            lineClamp={2}
                            onToggleTextSize={handleDescriptionSizeToggle}
                            isContentCentered
                        />
                    </StyledDescription>
                    <RoomsSlider bubbles={roomsWithLinks} onItemClick={handleRoomItemClick} />
                    <FilterAndSortBox handleFiltersClick={handleFiltersClick} />
                </LooksHeaderWrapper>
                <LooksGrid />
                <LooksNotFound />
            </PageWrapper>
        </ErrorBoundary>
    );
};

export default LooksListingPage;
